import type { TokenDef } from "../data/tokens.js";
import type { BuildTxSuccessPayload } from "../types.js";

import { formatTokenAmount } from "../lib/format.js";
import styles from "../styles/QuoteResult.module.css";

/**
 * Big-number presentation of a successful quote. The hero card answers
 * the user's primary question ("¿cuánto pago / cuánto recibo?"); the
 * secondary numbers (toAmountMin, estimatedGas, route) sit below it.
 *
 * Token decimals come from the **form props** (`fromToken` / `toToken`)
 * rather than being heuristically inferred from the response. The
 * BuildTxResponse doesn't carry the bridge token's metadata, and the
 * swap step's `tx.to` is the router address, NOT the output token —
 * inferring from there was the source of the "unknown token decimals"
 * fallback that surfaced in EXACT_OUT screenshots.
 */
export interface QuoteResultProps {
  readonly result: BuildTxSuccessPayload;
  /** Token the user paid in (`fromToken` selected in the form). */
  readonly fromToken: TokenDef;
  /** Token the user receives (`toToken` / bridge token selected in the form). */
  readonly toToken: TokenDef;
}

export function QuoteResult({
  result,
  fromToken,
  toToken,
}: QuoteResultProps): React.JSX.Element {
  const { quote, steps } = result;

  const fromLabel = formatTokenAmount(quote.fromAmount, fromToken);
  const toLabel = formatTokenAmount(quote.toAmount, toToken);
  const toMinLabel = formatTokenAmount(quote.toAmountMin, toToken);

  return (
    <section className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroSide}>
          <span className={styles.heroLabel}>Pagas</span>
          <strong className={styles.heroValue}>{fromLabel}</strong>
        </div>
        <span className={styles.heroArrow}>→</span>
        <div className={styles.heroSide}>
          <span className={styles.heroLabel}>Recibes</span>
          <strong className={styles.heroValue}>{toLabel}</strong>
        </div>
      </div>

      <dl className={styles.details}>
        <div className={styles.detailRow}>
          <dt>Mínimo recibido (tras slippage)</dt>
          <dd>{toMinLabel}</dd>
        </div>
        <div className={styles.detailRow}>
          <dt>Gas estimado</dt>
          <dd>{quote.estimatedGas}</dd>
        </div>
        <div className={styles.detailRow}>
          <dt>Coste estimado de gas</dt>
          <dd>
            {quote.gasCost !== undefined
              ? `$${quote.gasCost.amountUSD} (${quote.gasCost.nativeSymbol})`
              : "—"}
          </dd>
        </div>
        <div className={styles.detailRow}>
          <dt>Ruta</dt>
          <dd>
            {quote.route.tool}
            {quote.route.bridge !== undefined && (
              <> · bridge: {quote.route.bridge}</>
            )}
            {quote.route.exchange !== undefined && (
              <> · exchange: {quote.route.exchange}</>
            )}
          </dd>
        </div>
        <div className={styles.detailRow}>
          <dt>Chains</dt>
          <dd>
            {quote.fromChainId} → {quote.toChainId}
          </dd>
        </div>
        <div className={styles.detailRow}>
          <dt>Steps</dt>
          <dd>
            {steps.length} (
            {steps
              .map((s) => s.__typename.replace("Step", "").toLowerCase())
              .join(" + ")}
            )
          </dd>
        </div>
      </dl>
    </section>
  );
}
